/**
 * Turn a failed OTP verification into copy a person can act on.
 *
 * ## Why this exists
 *
 * Three sign-in surfaces each hand-rolled this, and all three reduced to the same line:
 *
 *     setError(isInvalidCode
 *       ? 'That code is invalid or expired. Please try again.'
 *       : backendMsg || 'That code is invalid or expired. Please try again.')
 *
 * Both branches end at the same string, so EVERY failure was reported as a bad code —
 * including failures that had nothing to do with the code.
 *
 * That is not a cosmetic problem. It happened for real: a missing unique index made the
 * server throw while provisioning the owner, AFTER the code had already been verified
 * successfully. Every owner sign-in failed, and every one of them was told their code was
 * invalid. The code was correct. Debugging started at the OTP and the OTP was never the
 * problem — the message sent people to the wrong place, and it stayed wrong for as long as
 * it took someone to read the server log instead of the screen.
 *
 * A wrong error message is worse than a vague one. "Your code is invalid" is a claim about
 * the user's input; when it is false, it makes them retype a correct code and doubt
 * themselves, and it hides the real fault from whoever is trying to fix it.
 *
 * ## The rule
 *
 * Only say the code is bad when the server actually SAID the code is bad. Anything else is
 * reported as what it is: something went wrong on our side.
 */

/**
 * A bare HTTP status reason ("Bad Request", "Forbidden") is framework noise, not copy.
 * Never render one.
 */
const isRawHttpStatus = (message: string | undefined): boolean =>
  !message ||
  /^(bad request|unauthorized|forbidden|not found|conflict|internal server error)$/i.test(
    message.trim(),
  );

/**
 * Server-side detail that must never reach a screen — a rejected query, a stack, an
 * internal identifier. The owner-provisioning failure surfaced its whole INSERT statement
 * as `message`, and the old code path would have rendered it verbatim to a customer.
 */
const isInternalDetail = (message: string): boolean =>
  /failed query|insert into|select .* from|drizzle|postgres|\bat [A-Z]\w+\.|node_modules/i.test(
    message,
  );

export const INVALID_CODE_MESSAGE = 'That code is invalid or expired. Please try again.';

/**
 * Deliberately does NOT tell the user to try again — if the server is failing, retrying
 * with the same correct code reproduces the same failure, and inviting it wastes their
 * time and teaches them to distrust a code that was fine.
 */
export const SIGN_IN_FAILED_MESSAGE =
  'We could not sign you in. This is a problem on our side, not with your code. Please contact support if it keeps happening.';

/**
 * @param err  the thrown request error (axios-shaped)
 * @returns    the message to show
 */
export const otpVerifyErrorMessage = (err: unknown): string => {
  const e = err as {response?: {status?: number; data?: {message?: unknown}}} | undefined;
  const status = e?.response?.status;
  const raw = e?.response?.data?.message;
  const backendMsg = typeof raw === 'string' ? raw : undefined;

  // 401 is the ONLY status that means "the code was rejected". The OTP check runs before
  // anything else in the flow, so a 401 here really is the code — and a 500 really is not.
  //
  // The `magic auth` / `one-time code` test is kept because WorkOS's own wording arrives
  // that way and is genuinely an invalid-code case, but it is now a POSITIVE signal only:
  // failing it no longer falls through to the invalid-code message.
  const codeWasRejected =
    status === 401 || /magic auth|one-time code/i.test(backendMsg ?? '');

  if (codeWasRejected) return INVALID_CODE_MESSAGE;

  // Not the code. Show the server's explanation only if it is fit to read — otherwise say
  // plainly that the fault is ours. Silence about the cause beats a confident lie about it.
  if (backendMsg && !isRawHttpStatus(backendMsg) && !isInternalDetail(backendMsg)) {
    return backendMsg;
  }

  return SIGN_IN_FAILED_MESSAGE;
};

/** The send-a-code step. Same rule: never blame a code that was never entered. */
export const otpSendErrorMessage = (err: unknown): string => {
  const e = err as {response?: {data?: {message?: unknown}}} | undefined;
  const raw = e?.response?.data?.message;
  const backendMsg = typeof raw === 'string' ? raw : undefined;

  if (backendMsg && !isRawHttpStatus(backendMsg) && !isInternalDetail(backendMsg)) {
    return backendMsg;
  }
  return 'We could not send the code. Please try again in a moment.';
};
